import { SafeImage } from "@/components/ui/SafeImage";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fuel, MapPin, Pencil, Share2 } from "lucide-react";
import type { GasStation } from "@/types/database";
import { formatFuelPrice } from "@/lib/utils";
import { resolveStationLogo } from "@/lib/station-logos";
import { useDeleteGasStation } from "@/lib/queries";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import { GasStationMapPreview } from "@/components/gas-stations/GasStationCard";
import { EditGasStationModal } from "@/components/gas-stations/EditGasStationModal";

export function GasStationDetailPanel({ station }: { station: GasStation }) {
  const navigate = useNavigate();
  const deleteStation = useDeleteGasStation();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleting = deleteStation.isPending;

  const prices: Array<[string, number | null]> = [
    ["E-10 87", station.fuelPrice_87],
    ["E-10 90", station.fuelPrice_90],
    ["Diesel", station.fuelPrice_Diesel],
    ["ULSD", station.fuelPrice_ULSD],
  ];

  const hasCoords = station.coordinatesLat != null && station.coordinatesLong != null;
  const logoSrc = resolveStationLogo(station);

  async function handleDelete() {
    setDeleteError(null);
    try {
      await deleteStation.mutateAsync(station.id);
      setDeleteOpen(false);
      navigate("/gas-stations");
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Failed to delete station");
    }
  }

  return (
    <>
      <Workspace>
        <WorkspaceSection className="grid gap-0 lg:grid-cols-[1fr_1.1fr]">
          <div className="border-b border-border p-4 sm:p-5 lg:border-b-0 lg:border-r">
            <div className="flex items-start justify-between gap-4">
              <p className="text-xs text-muted">
                {station.name} · {station.parish ?? "Jamaica"}
              </p>
              <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            </div>

            <div className="mt-2 flex items-start justify-between gap-3">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {formatFuelPrice(station.fuelPrice_87)}
                <span className="text-base font-semibold text-muted"> /87</span>
              </h1>
              <button
                type="button"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f5f5f7] transition hover:bg-[#ebebed]"
              >
                <Share2 className="h-4 w-4" />
              </button>
            </div>

            <p className="mt-2 text-xs text-muted">{station.address}</p>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {station.fuelType ? (
                <span className="stat-pill">
                  <Fuel className="h-3.5 w-3.5" />
                  {station.fuelType}
                </span>
              ) : null}
              <span className="stat-pill">
                <MapPin className="h-3.5 w-3.5" />
                {hasCoords ? "Mapped" : "No coords"}
              </span>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2.5">
              {prices.map(([label, value]) => (
                <div key={label} className="rounded-xl bg-[#f5f5f7] p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted">{label}</p>
                  <p className="mt-0.5 text-base font-bold">{formatFuelPrice(value)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex min-h-[200px] items-center justify-center bg-[#fafafa] p-5 lg:min-h-[280px]">
            {logoSrc ? (
              <SafeImage src={logoSrc} alt={station.name} width={120} height={120} className="object-contain" />
            ) : (
              <span className="text-3xl font-bold text-[#d2d2d7]">{station.name.slice(0, 2)}</span>
            )}
          </div>
        </WorkspaceSection>

        <WorkspaceSection border className="grid gap-0 lg:grid-cols-[1fr_1.1fr]">
          <div className="border-b border-border p-4 sm:p-5 lg:border-b-0 lg:border-r">
            <h2 className="mb-4 font-bold">Location</h2>
            <GasStationMapPreview station={station} />
          </div>

          <div className="p-4 sm:p-5">
            <h2 className="font-bold">Station details</h2>
            <p className="mt-1 text-sm text-muted">
              Update fuel prices, address, coordinates, and upload a station logo.
            </p>
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between gap-4 border-b border-border/60 pb-3">
                <dt className="text-muted">ID</dt>
                <dd className="font-semibold text-foreground">{station.id}</dd>
              </div>
              {station.slug ? (
                <div className="flex justify-between gap-4 border-b border-border/60 pb-3">
                  <dt className="text-muted">Slug</dt>
                  <dd className="font-semibold text-foreground">{station.slug}</dd>
                </div>
              ) : null}
              <div className="flex justify-between gap-4">
                <dt className="text-muted">Logo</dt>
                <dd className="font-semibold text-foreground">{logoSrc ? "Uploaded" : "Fallback brand logo"}</dd>
              </div>
            </dl>
            <Button variant="brand" className="mt-6 w-full" onClick={() => setEditOpen(true)}>
              Edit station
            </Button>
            <Button
              variant="danger"
              className="mt-3 w-full"
              onClick={() => {
                setDeleteError(null);
                setDeleteOpen(true);
              }}
              disabled={deleting}
            >
              Delete station
            </Button>
          </div>
        </WorkspaceSection>
      </Workspace>

      <EditGasStationModal station={station} open={editOpen} onClose={() => setEditOpen(false)} />

      <Modal
        open={deleteOpen}
        onClose={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteError(null);
        }}
        title="Delete station"
        className="max-w-lg"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteError(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete station"}
            </Button>
          </>
        }
      >
        {deleteError ? (
          <p className="mb-4 rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{deleteError}</p>
        ) : null}
        <p className="text-foreground">
          Are you sure you want to delete <span className="font-bold">{station.name}</span> at{" "}
          <span className="font-bold">{station.address}</span>? This action cannot be undone.
        </p>
      </Modal>
    </>
  );
}
